import { AxiosResponse } from 'axios';
import _toUpper from 'lodash/toUpper';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import {
  RbacBundledRolesResponseType,
  RbacCustomRolesResponseType,
} from 'src/app/interfaces/IUserRoles';
import { getCombinedRoles } from 'src/app/utils/shared';
import remoteSdk from 'src/platform-sdk/initialized-platform-sdk';
import {
  ClickAway,
  CloseErrorBanners,
  FetchAllGroups,
  FetchAllOrganizations,
  FetchAllProductsInstances,
  FetchAllRoles,
  FetchAllUsers,
  FetchFeatureFlags,
  FetchTenantId,
} from './actions';
import {
  filterMultipleInstanceProducts,
  filterNonSystemGroups,
} from './helpers';
import { getAuth, getFeatureFlagsSet, getNavigation } from './selectors';
import { phanesAPIClient, prometheusAPIClient } from '../../api/clients';
import { IDataProps } from '../../config/interfaces';
import { getConfigData } from '../../config/selectors';
import { IActionWithPayload } from '../../core/redux/interfaces';
import { AppliesTo } from '../../enums/Groups';
import {
  IFetchAllProductsInstances,
  IFetchGroupsResponseType,
  IGroup,
  IUser,
  INavigation,
} from '../../interfaces/ICommon';
import { notifyFormPopulated } from '../../services/formPopulated';
import {
  FEATURE_FLAG_CONSTANTS,
  FEATURE_FLAG_PREFIX,
  MSP_CONSTANTS,
  SECURITY_CLOUD_CONTROL_READ_ONLY,
} from '../../utils/constant';
import {
  getEnterpriseDetails,
  getEnterpriseId,
} from '../enterprises/selectors';
import {
  ClearEditGroup,
  ClearMapGroupsStepper,
  CloseErrorBanners as CloseGroupsErrorBanner,
  CloseMapGroupsErrorBanner,
  DiscardAddGroupChanges,
  HideDiscardChangesModal,
  ShowDiscardChangesModal,
} from '../groups/actions';
import { getEditedGroupState, getIsAddGroupDirty } from '../groups/selectors';
import {
  ClearEditingUserRole,
  ClearRolesTableConfigData,
  ClearSelectedUserRoles,
  ClearSelectedUserRolesTableFilters,
  DiscardCustomRoleStepper,
  HideUserRoleNotification,
  ToggleDiscardChangesModal as ToggleDiscardCustomRoleModal,
} from '../user-roles/actions';
import { getIsCustomRoleDirty } from '../user-roles/selectors';
import {
  ClearUsers,
  DiscardInviteChanges,
  ToggleDiscardChangesModal as ToggleDiscardUserDetailsModal,
} from '../users/actions';
import { RbacRolesResponseType } from '../users/interfaces';
import { populateRolesSelect } from '../users/populateRolesSelect';
import { getIsUserDetailsDirty } from '../users/selectors';

type TFetchAllProductsInstancesResp =
  | AxiosResponse<{ data: IFetchAllProductsInstances }>
  | any;

const isManagerOrgWithAdminGroup = (
  hasMspAssignRoleWithoutInstance: boolean,
  enterpriseDetails: any,
  editedGroupState: { data: IGroup }
): boolean => {
  const enterpriseType = (
    enterpriseDetails as { details?: { enterpriseType?: string } }
  )?.details?.enterpriseType;

  const isManagerOrg =
    hasMspAssignRoleWithoutInstance &&
    (enterpriseType === MSP_CONSTANTS.multiOrgManager ||
      enterpriseType === MSP_CONSTANTS.mspManager);

  const appliesTo = editedGroupState?.data?.appliesTo;

  const isAdminGroup =
    appliesTo === AppliesTo.ALL_MANAGED || appliesTo === AppliesTo.SOME_MANAGED;

  return Boolean(isManagerOrg && isAdminGroup);
};

export function* handleClickAway(
  action: IActionWithPayload<{
    origin: string;
    shouldRedirect: boolean;
    path: string;
  }>
): Generator {
  const {
    payload: { shouldRedirect = true, path },
  } = action;
  const { signOutCallbackFn } = (yield select(getAuth)) as any;
  const { navigateFn, redirectPath } = (yield select(
    getNavigation
  )) as INavigation;

  const isGroupsActive = path.includes('groups');
  const isEditGroupActive = path.includes('edit-group');
  const isUsersActive = path.includes('users') && !path.includes('edit-user');
  const isUserRolesActive =
    path.includes('user-roles') || path.includes('edit-user-role');

  const dirtyStates = {
    isAddGroupDirty: (yield select(getIsAddGroupDirty)) as boolean,
    isUserDetailsDirty: (yield select(getIsUserDetailsDirty)) as boolean,
    isCustomRoleDirty: (yield select(getIsCustomRoleDirty)) as boolean,
  };

  if (isGroupsActive && dirtyStates.isAddGroupDirty) {
    yield put(ShowDiscardChangesModal());
    return;
  }

  if (isUsersActive && dirtyStates.isUserDetailsDirty) {
    yield put(ToggleDiscardUserDetailsModal(true));
    return;
  }

  if (isUserRolesActive && dirtyStates.isCustomRoleDirty) {
    yield put(ToggleDiscardCustomRoleModal(true));
    return;
  }

  if (signOutCallbackFn) {
    yield call(signOutCallbackFn);
    return;
  }

  if (shouldRedirect) {
    yield call(handleRedirect as any, {
      isGroupsActive,
      isEditGroupActive,
      isUsersActive,
      isUserRolesActive,
      dirtyStates,
      navigateFn,
      redirectPath,
    });
  }
}

function* handleRedirect({
  isGroupsActive,
  isEditGroupActive,
  isUsersActive,
  isUserRolesActive,
  dirtyStates,
  navigateFn,
  redirectPath,
}: {
  isGroupsActive: boolean;
  isEditGroupActive: boolean;
  isUsersActive: boolean;
  isUserRolesActive: boolean;
  dirtyStates: {
    isAddGroupDirty: boolean;
    isUserDetailsDirty: boolean;
    isCustomRoleDirty: boolean;
  };
  navigateFn: Function;
  redirectPath: string;
}): Generator {
  if (isGroupsActive) {
    if (!dirtyStates.isAddGroupDirty) {
      yield put(ClearMapGroupsStepper());
    }
    yield put(CloseMapGroupsErrorBanner());
  }

  if (isUsersActive && !dirtyStates.isUserDetailsDirty) {
    yield put(ClearUsers());
  }

  if (isUserRolesActive) {
    yield put(ClearRolesTableConfigData());
    yield put(ClearSelectedUserRoles());
    yield put(ClearEditingUserRole());
    yield put(HideUserRoleNotification());
    yield put(ClearSelectedUserRolesTableFilters());
  }

  if (isEditGroupActive) {
    yield put(ClearEditGroup());
  }

  yield call(navigateFn as (path: string) => void, redirectPath);
}

export function* handleFetchFeatureFlags(): Generator {
  const enterpriseId = (yield select(getEnterpriseId)) as string;
  try {
    if (!enterpriseId) {
      return;
    }

    const response = (yield prometheusAPIClient.get<string[]>(
      `/api/featureFlag/${enterpriseId}`
    )) as AxiosResponse<string[], any>;
    yield put(FetchFeatureFlags.SUCCESS(response.data));
  } catch (error) {
    yield put(FetchFeatureFlags.ERROR(error as Error));
  }
}

export function* handleFetchTenantId(): Generator {
  if (
    !remoteSdk.featureFlags.isEnabled(FEATURE_FLAG_CONSTANTS.ENABLE_CUI_MFE)
  ) {
    return;
  }

  const enterpriseId = yield select(getEnterpriseId);
  try {
    const response = yield prometheusAPIClient.get(
      `/api/enterprises/${enterpriseId}/cuiDetail`
    );
    yield put(FetchTenantId.SUCCESS(response.data.id));
  } catch {
    yield put(FetchTenantId.ERROR(new Error('Failed to fetch tenant ID')));
  }
}

export function* handleDiscardChanges(
  action: IActionWithPayload<{ shouldRedirect: boolean }>
): Generator {
  const { payload: { shouldRedirect = true } = {} } = action;

  const { redirectPath, navigateFn, pathname } = (yield select(
    getNavigation
  )) as any;
  const { signOutCallbackFn } = (yield select(getAuth)) as any;

  yield call(notifyFormPopulated, { isDirty: false });

  if (pathname.includes('groups')) {
    yield put(HideDiscardChangesModal());
  }

  if (pathname.includes('users')) {
    yield put(ToggleDiscardUserDetailsModal(false));
  }

  if (pathname.includes('user-roles')) {
    yield put(ToggleDiscardCustomRoleModal(false));
  }

  if (signOutCallbackFn) {
    yield call(signOutCallbackFn);
    return;
  }

  if (shouldRedirect) {
    yield call(navigateFn, redirectPath);
  }
}

export function* handleFetchAllProductsInstances(): Generator {
  const enterpriseId = yield select(getEnterpriseId);
  const config = yield select(getConfigData);

  try {
    const env = (config as IDataProps)?.env;
    const response: TFetchAllProductsInstancesResp = yield phanesAPIClient.get(
      `v1/enterprise/${enterpriseId}/products`
    );

    // TODO: remove this condition once CN envs supports single-tenant policy
    if (
      env &&
      ['cn-ci', 'cn-preview', 'cn-staging', 'cn-prod'].includes(
        env.toLowerCase()
      )
    ) {
      yield put(
        FetchAllProductsInstances.SUCCESS(response.data.productInstancesInfo)
      );
    } else {
      const { productInstancesInfo, multipleProductTypes } =
        filterMultipleInstanceProducts(response.data.productInstancesInfo);
      localStorage.setItem(
        'MultipleProductTypes',
        JSON.stringify(multipleProductTypes)
      );

      yield put(FetchAllProductsInstances.SUCCESS(productInstancesInfo));
    }
  } catch (error) {
    yield put(FetchAllProductsInstances.ERROR(error as Error));
  }
}

export function* handleFetchAllUsers(): Generator {
  const enterpriseId = yield select(getEnterpriseId);
  try {
    const response: IUser[] | any = yield prometheusAPIClient.get(
      `/api/enterprises/${enterpriseId}/users`
    );
    yield put(FetchAllUsers.SUCCESS(response.data.items));
  } catch (error) {
    yield put(FetchAllUsers.ERROR(error as Error));
  }
}

export function* handleCloseErrorBanners(): Generator {
  yield put(CloseGroupsErrorBanner());
}

export function* handleFetchAllGroups(): Generator {
  const enterpriseId = (yield select(getEnterpriseId)) as string;

  yield call(handleFetchFeatureFlags);
  const featureFlagsSet = (yield select(getFeatureFlagsSet)) as Set<string>;

  try {
    const response = (yield prometheusAPIClient.get(
      `/api/enterprises/${enterpriseId}/rbac/groups`
    )) as AxiosResponse<IFetchGroupsResponseType>;
    const rbacGroups: IGroup[] = response.data.rbacGroups;
    const hasSystemGroupFeatureFlag = featureFlagsSet.has(
      FEATURE_FLAG_CONSTANTS.IAM_SYSTEM_GROUPS_10194
    );
    const filteredGroups = filterNonSystemGroups(rbacGroups);
    yield put(
      FetchAllGroups.SUCCESS(
        hasSystemGroupFeatureFlag ? rbacGroups : filteredGroups
      )
    );
  } catch (error) {
    yield put(FetchAllGroups.ERROR(error as Error));
  }
}

export function* handleFetchAllRoles(): Generator {
  const enterpriseId = yield select(getEnterpriseId);

  yield call(handleFetchFeatureFlags);
  const featureFlagsSet = (yield select(getFeatureFlagsSet)) as Set<string>;
  const enterpriseDetails = yield select(getEnterpriseDetails);
  const editedGroupState = (yield select(getEditedGroupState)) as {
    data: IGroup;
  };

  const hasMspAssignRoleWithoutInstance = featureFlagsSet.has(
    FEATURE_FLAG_CONSTANTS?.MSP_ASSIGN_ROLE_WITHOUT_INSTANCE
  );

  const skipRoleFeatureFlagFiltering = isManagerOrgWithAdminGroup(
    hasMspAssignRoleWithoutInstance,
    enterpriseDetails,
    editedGroupState
  );

  try {
    const response: RbacRolesResponseType = yield prometheusAPIClient.get(
      `/api/enterprises/${enterpriseId}/rbac/roles`
    );
    const customRolesResponse: RbacCustomRolesResponseType =
      yield prometheusAPIClient.get(
        `/api/enterprises/${enterpriseId}/rbac/customRoles`
      );
    const bundledRolesResponse: RbacBundledRolesResponseType =
      yield prometheusAPIClient.get(
        `/api/enterprises/${enterpriseId}/rbac/bundledRoles`
      );

    const combinedRoles = getCombinedRoles(
      response,
      customRolesResponse,
      featureFlagsSet.has(FEATURE_FLAG_CONSTANTS.BUNDLED_ROLE)
        ? bundledRolesResponse
        : null
    );

    const rbacRoles = skipRoleFeatureFlagFiltering
      ? combinedRoles
      : combinedRoles.filter((role) =>
          new Set(
            role.featureFlags?.map((flag) =>
              _toUpper(flag).replace(FEATURE_FLAG_PREFIX, '')
            )
          ).isSubsetOf(featureFlagsSet)
        );

    const rolesWithFilteredReadOnly = featureFlagsSet.has(
      FEATURE_FLAG_CONSTANTS.SCC_READ_ONLY
    )
      ? rbacRoles
      : rbacRoles.filter(
          (role) => role.roleDisplayName !== SECURITY_CLOUD_CONTROL_READ_ONLY
        );

    yield put(FetchAllRoles.SUCCESS(rolesWithFilteredReadOnly));
    yield populateRolesSelect();
  } catch (error) {
    yield put(FetchAllRoles.ERROR(error as Error));
  }
}

export function* handleFetchAllOrganizations(): Generator {
  const enterpriseId = (yield select(getEnterpriseId)) as string;

  try {
    const response: any = yield prometheusAPIClient.get(
      `/api/enterprises/${enterpriseId}/managed`
    );

    yield put(FetchAllOrganizations.SUCCESS(response.data));
  } catch (error) {
    yield put(FetchAllOrganizations.ERROR(error as Error));
  }
}

export function* commonSaga(): Generator {
  yield takeEvery(ClickAway.TYPE, handleClickAway);
  yield takeEvery(
    [
      DiscardInviteChanges.TYPE,
      DiscardAddGroupChanges.TYPE,
      DiscardCustomRoleStepper.TYPE,
    ],
    handleDiscardChanges
  );
  yield takeLatest(
    FetchAllProductsInstances.Pending.TYPE,
    handleFetchAllProductsInstances
  );
  yield takeLatest(FetchAllUsers.Pending.TYPE, handleFetchAllUsers);
  yield takeLatest(FetchAllRoles.Pending.TYPE, handleFetchAllRoles);
  yield takeLatest(FetchFeatureFlags.Pending.TYPE, handleFetchFeatureFlags);
  yield takeLatest(FetchTenantId.Pending.TYPE, handleFetchTenantId);
  yield takeLatest(CloseErrorBanners.TYPE, handleCloseErrorBanners);
  yield takeLatest(FetchAllGroups.Pending.TYPE, handleFetchAllGroups);
  yield takeLatest(
    FetchAllOrganizations.Pending.TYPE,
    handleFetchAllOrganizations
  );
}
